import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { countBusinessDays } from '@domain/policies/BusinessDayPolicy';
import {
  MAX_HOUR_PERMIT_HOURS,
  hourWindowMinutes,
  type PermissionKind,
} from '@domain/entities/PermissionRequest';
import { DateRange } from '@domain/value-objects/DateRange';
import { getServices } from '@infrastructure/services';
import { Chip, cx } from '../../design-system';
import { formatDateRange, formatDay, formatHourWindow } from '../../format';
import { useAreaKinds } from '../../hooks/useAreaKinds';
import { useSession } from '../../providers/SessionProvider';
import { useToast } from '../../providers/ToastProvider';
import { WizardShell, wizardStyles as w } from '../../wizard/WizardShell';
import { RangePicker } from '../../wizard/RangePicker';
import styles from './AbsenceWizards.module.css';

/**
 * Nuevo permiso — the three-step flow, transcribed from
 * `design/reference/screens/nuevo-permiso.html`.
 *
 * Separate from the vacation wizard rather than a mode of it, because what the
 * steps ask differs at every point: the kind is chosen first and then governs
 * whether the flow measures days or hours, whether the time is paid, and which
 * warning applies. Sharing one wizard would mean a step that changes its own
 * meaning halfway through.
 *
 * The kinds come from the runtime catalogue, so RR.HH. can add one without a
 * redeploy — which is why nothing here switches on a hard-coded kind name
 * except the two warnings the handoff spells out.
 */

const HOME_OFFICE_WEEKLY_LIMIT = 2;
const HOUR_PERMIT_LIMIT_MINUTES = MAX_HOUR_PERMIT_HOURS * 60;

/*
 * Cuántas horas se puede salir antes, como opciones y no como un campo libre.
 *
 * Una salida anticipada no se pide «a las 16:00»: se pide «dos horas antes», y a
 * qué hora es eso depende de la jornada de cada quien. Un desplegable de cuatro
 * opciones dice a la vez cuánto se puede pedir y cuál es el techo, sin que haya
 * que escribir algo para que el formulario lo rechace.
 */
const EARLY_HOUR_CHOICES: readonly number[] = Array.from(
  { length: MAX_HOUR_PERMIT_HOURS },
  (_, index) => index + 1,
);

const STEPS = [
  { label: 'Tipo de permiso' },
  { label: 'Fechas y motivo' },
  { label: 'Resumen' },
] as const;

export const PermissionWizard = ({
  onClose,
  onSubmitted,
}: {
  onClose: () => void;
  onSubmitted: () => Promise<void>;
}): ReactNode => {
  const session = useSession();
  const toast = useToast();

  /* Los de su área, no los de la empresa: cada área decide cuáles ofrece y puede
     tener los suyos. Ver `useAreaKinds`. */
  const { permissionKinds } = useAreaKinds(session.areaId);
  const kinds = useMemo(() => permissionKinds.filter((k) => k.enabled), [permissionKinds]);

  const [step, setStep] = useState(0);
  const [kindId, setKindId] = useState(() => kinds[0]?.id ?? '');
  const [range, setRange] = useState<DateRange | null>(null);
  const [from, setFrom] = useState('09:00');
  const [to, setTo] = useState('13:00');
  /** Horas de salida anticipada. Dos es lo que más se pide y evita un paso. */
  const [earlyHours, setEarlyHours] = useState(2);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const today = getServices().clock.today();

  const holidays = useQuery({
    queryKey: ['wizard', 'holidays', today.year],
    queryFn: async () => {
      const result = await getServices().holidays.nonWorkingDates([today.year, today.year + 1]);
      return result.ok ? result.value : new Set<string>();
    },
  });

  const nonWorking = holidays.data ?? new Set<string>();

  /** Falls back to the first enabled kind if the chosen one was just disabled. */
  const kind: PermissionKind | undefined = kinds.find((k) => k.id === kindId) ?? kinds[0];
  const byHours = kind?.measuredInHours === true;
  /*
   * Un permiso de horas que no vuelve —«Salir temprano»—. La bandera viene del
   * catálogo y no de reconocer el nombre: ver `PermissionKind.openEnded`.
   */
  const openEnded = byHours && kind?.openEnded === true;
  /*
   * Si este tipo se puede pedir para días que ya pasaron.
   *
   * Paternidad y fallecimiento se registran después de que ocurrió lo que los
   * motiva. El resto va por adelantado, y el calendario deja de ofrecer lo que el
   * servidor rechazaría — que es la mitad que faltaba: la regla estaba sólo aquí y
   * una regla que sólo vive en la pantalla se salta con una llamada directa.
   */
  const retroactive = kind?.retroactive === true;

  /**
   * Lo que se guarda, en una de las dos formas que se escriben.
   *
   * Sin regreso van sólo las horas: la de salida que quedó en el estado no
   * describe nada en ese permiso, y guardarla se leería como un compromiso de
   * salir a esa hora. Ver `HourWindow`.
   */
  const hourWindow = useMemo(
    () =>
      openEnded
        ? { from: null, to: null, earlyHours }
        : { from, to, earlyHours: null },
    [from, to, openEnded, earlyHours],
  );

  const businessDays = range === null ? 0 : countBusinessDays(range, nonWorking);
  const minutes = hourWindowMinutes(hourWindow);
  const count = byHours ? minutes / 60 : businessDays;

  /*
   * La vuelta antes de la salida, dicha donde se escribe y no al pulsar Continuar.
   *
   * El formulario ya lo rechazaba, pero sólo al avanzar y con un aviso flotante;
   * mientras tanto el panel marcaba «HORAS 0» sin explicar por qué. QA lo reportó
   * como que «permite ponerlo», y tiene razón: permitirlo y quejarse después no es
   * lo mismo que impedirlo.
   */
  const invertedWindow = byHours && !openEnded && minutes <= 0;

  /**
   * The two policy notices from the handoff.
   *
   * Both are warnings, not blocks — a leader may authorise the exception, and
   * refusing to send it would move that decision from the person who has the
   * authority to the form.
   */
  const warning = useMemo(() => {
    if (kind === undefined) return null;
    if (kind.id === 'Home office' && businessDays > HOME_OFFICE_WEEKLY_LIMIT) {
      return `La política permite hasta ${String(
        HOME_OFFICE_WEEKLY_LIMIT,
      )} días remotos por semana; el líder deberá autorizar la excepción.`;
    }
    if (byHours && minutes > HOUR_PERMIT_LIMIT_MINUTES) {
      return 'Los permisos de horas se limitan a 4 h; el resto se descuenta como día personal.';
    }
    return null;
  }, [kind, byHours, businessDays, minutes]);

  const summary = useMemo(() => {
    if (kind === undefined || range === null) return [];
    return [
      { k: 'Tipo', v: kind.id },
      {
        k: byHours ? 'Horario' : 'Periodo',
        v: byHours
          ? `${formatDay(range.start)} · ${formatHourWindow(hourWindow)}`
          : `${formatDateRange(range)} · ${String(businessDays)} ${
              businessDays === 1 ? 'día hábil' : 'días hábiles'
            }`,
      },
      {
        k: 'Goce de sueldo',
        v: kind.withPay ? 'Con goce de sueldo' : 'Sin goce, con descuento en nómina',
      },
      { k: 'Motivo', v: reason.trim().length > 0 ? reason.trim() : '—' },
    ];
  }, [kind, range, byHours, hourWindow, businessDays, reason]);

  const advance = (): void => {
    if (step === 0) {
      setStep(1);
      return;
    }

    if (step === 1) {
      if (range === null) {
        toast.warn('Elige al menos un día en el calendario.');
        return;
      }
      if (!byHours && businessDays === 0) {
        toast.warn('Revisa las fechas: elige al menos un día hábil.');
        return;
      }
      if (invertedWindow) {
        toast.warn('La hora de regreso debe ser posterior a la de salida.');
        return;
      }
      if (reason.trim().length === 0) {
        toast.warn('Falta el motivo: tu líder necesita el contexto para autorizar.');
        return;
      }
      setStep(2);
      return;
    }

    void submit();
  };

  const submit = async (): Promise<void> => {
    if (kind === undefined || range === null) return;
    setBusy(true);

    const result = await getServices().permissions.submit({
      kind: kind.id,
      // An hour permit is a single day; sending the whole picked range would
      // reserve days the person never asked for.
      range: byHours ? DateRange.singleDay(range.start) : range,
      hours: byHours ? hourWindow : null,
      reason: reason.trim(),
    });

    setBusy(false);

    if (!result.ok) {
      toast.bad(`No pudimos enviar el permiso. ${describe(result.error)}`);
      return;
    }

    toast.ok('Permiso enviado. Tu líder de sub área recibió la solicitud.');
    await onSubmitted();
    onClose();
  };

  return (
    <WizardShell
      eyebrow="Flujo de permiso"
      title="Nuevo permiso u home office"
      steps={STEPS}
      current={step}
      nextLabel={step === 2 ? 'Enviar al líder →' : 'Continuar →'}
      nextDisabled={kinds.length === 0}
      busy={busy}
      onBack={() => {
        setStep((s) => Math.max(0, s - 1));
      }}
      onNext={advance}
      onClose={onClose}
    >
      {step === 0 && (
        <>
          <h2 className={w.stepTitle}>¿Qué necesitas?</h2>
          <p className={w.stepNote}>Cada tipo tiene su propia regla de aviso y de goce de sueldo.</p>

          <div className={styles.kindList}>
            {kinds.map((option) => {
              const picked = option.id === kind?.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  className={cx(styles.kindCard, picked && styles.kindCardPicked)}
                  onClick={() => {
                    setKindId(option.id);
                  }}
                  aria-pressed={picked}
                >
                  <span className={cx(styles.kindIcon, picked && styles.kindIconPicked)}>
                    {option.icon}
                  </span>
                  <span className={styles.kindText}>
                    <span className={styles.kindLabel}>{option.id}</span>
                    <span className={styles.kindNote}>{option.note}</span>
                  </span>
                  <Chip
                    background={picked ? 'var(--color-success-bg)' : 'var(--color-surface-subtle-alt)'}
                    foreground={
                      picked ? 'var(--color-success-ink)' : 'var(--color-text-tertiary-alt)'
                    }
                  >
                    {picked ? 'Elegido' : 'Elegir'}
                  </Chip>
                </button>
              );
            })}
          </div>
        </>
      )}

      {step === 1 && (
        <>
          <h2 className={w.stepTitle}>{byHours ? 'Día y horario' : 'Desde y hasta'}</h2>
          <p className={w.stepNote}>
            {byHours
              ? 'Elige el día y la ventana de horas que necesitas.'
              : 'Los fines de semana y feriados no cuentan como días hábiles.'}
          </p>

          {/*
            El calendario a la izquierda y lo demás a su derecha.

            Estaban en una sola columna, y el calendario está topado a 22rem
            porque sus celdas son cuadradas: en el modal ancho eso dejaba media
            pantalla vacía a la derecha mientras las horas y el motivo caían tan
            abajo que había que desplazarse para verlos. Ahora el resto del paso
            ocupa ese hueco y el paso entero se lee sin scroll. Por debajo de
            ~40rem la rejilla vuelve a una columna sola.
          */}
          <div className={styles.pickerSplit}>
            <RangePicker
              value={range}
              onChange={setRange}
              /* Un año hacia atrás cuando el tipo lo admite, la misma ventana que
                 usan las incapacidades: cubre un registro tardío sin abrir el
                 calendario entero. */
              minimum={retroactive ? today.addMonths(-12) : today}
              holidays={nonWorking}
              busy={new Map()}
            />

            <div className={styles.pickerAside}>
              {/* Ámbar cuando el rango está al revés: un «0» en verde se lee como
                  un dato y no como un error. */}
              <div className={cx(styles.countPanel, invertedWindow && styles.countPanelAmber)}>
                <div className={styles.countLabel}>{byHours ? 'Horas' : 'Días hábiles'}</div>
                <div className={styles.countValue}>
                  {byHours ? count.toLocaleString('es-MX', { maximumFractionDigits: 1 }) : count}
                </div>
              </div>

              {/*
                Un permiso de horas se pide con dos relojes; una salida anticipada,
                con un número de horas. No es la misma pregunta con un campo menos:
                a qué hora sales si te vas dos horas antes depende de tu jornada, y
                el portal no la tiene guardada en ningún sitio.
              */}
              {byHours &&
                (openEnded ? (
                  <label className={styles.field}>
                    <span className={styles.fieldLabel}>¿Cuánto quieres salir antes?</span>
                    <select
                      className={styles.select}
                      value={String(earlyHours)}
                      onChange={(e) => {
                        setEarlyHours(Number(e.target.value));
                      }}
                    >
                      {EARLY_HOUR_CHOICES.map((hours) => (
                        <option key={hours} value={String(hours)}>
                          {hours} {hours === 1 ? 'hora antes' : 'horas antes'}
                        </option>
                      ))}
                    </select>
                    <span className={styles.hourNote}>
                      No regresas ese día. El máximo son {MAX_HOUR_PERMIT_HOURS} horas;
                      más que eso ya es un día personal.
                    </span>
                  </label>
                ) : (
                  <div className={styles.hourRow}>
                    <label className={styles.field}>
                      <span className={styles.fieldLabel}>Hora de salida</span>
                      <input
                        type="time"
                        className={styles.input}
                        value={from}
                        onChange={(e) => {
                          setFrom(e.target.value);
                        }}
                      />
                    </label>
                    <label className={styles.field}>
                      <span className={styles.fieldLabel}>Hora de regreso</span>
                      <input
                        type="time"
                        /* `min` para que el selector nativo no ofrezca lo que el
                           formulario va a rechazar; el aviso de abajo por si se
                           escribe a mano, que es por donde entra. */
                        min={from}
                        className={cx(styles.input, invertedWindow && styles.inputInvalid)}
                        aria-invalid={invertedWindow}
                        value={to}
                        onChange={(e) => {
                          setTo(e.target.value);
                        }}
                      />
                    </label>
                  </div>
                ))}

              {invertedWindow && (
                <p className={styles.fieldError} role="alert">
                  La hora de regreso debe ser posterior a la de salida.
                </p>
              )}

              <label className={styles.field}>
                <span className={styles.fieldLabel}>Motivo</span>
                <textarea
                  className={styles.textarea}
                  rows={3}
                  value={reason}
                  placeholder="Explica brevemente por qué lo necesitas."
                  onChange={(e) => {
                    setReason(e.target.value);
                  }}
                />
              </label>

              {warning !== null && <div className={styles.warnBand}>{warning}</div>}
            </div>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <h2 className={w.stepTitle}>Resumen</h2>
          <p className={w.stepNote}>Revisa antes de enviar; después sólo podrás cancelarlo.</p>

          <dl className={styles.summary}>
            {summary.map((row) => (
              <div key={row.k} className={styles.summaryRow}>
                <dt className={styles.summaryKey}>{row.k}</dt>
                <dd className={styles.summaryValue}>{row.v}</dd>
              </div>
            ))}
          </dl>

          <p className={styles.footNote}>
            Al enviar, tu líder de sub área recibe el aviso y verás el estado en Mis ausencias.
          </p>
        </>
      )}

      {kinds.length === 0 && (
        <p className={styles.footNote}>
          No hay tipos de permiso activos. Pide a RR.HH. que habilite al menos uno en Catálogos.
        </p>
      )}
    </WizardShell>
  );
};

const describe = (error: { kind: string }): string => {
  switch (error.kind) {
    case 'permission-denied':
      return 'Tu perfil no puede registrar permisos.';
    case 'unavailable':
      return 'No pudimos conectar. Revisa tu red e intenta de nuevo.';
    default:
      return 'Intenta de nuevo en un momento.';
  }
};
